// ============================================================
// VCS.NetStats — Live-Verbindungsqualität im Call-Screen
// ============================================================
// Zeigt ein Signal-Stärke-Icon (3 Balken) im Call an.
// Farbe wechselt basierend auf RTT + Paketverlust:
//   Grün  = gut  |  Gelb = mäßig  |  Rot = schlecht
// Bei schlechter Verbindung erscheint ein Reconnect-Button.
// ============================================================
window.VCS = window.VCS || {};

VCS.NetStats = class {

    constructor() {
        this._interval = null;
        this._el = null;
        this._reconnectBtn = null;
        this._lastPacketsReceived = 0;
        this._lastPacketsLost = 0;
        this._currentLevel = 'good';
        this._badSince = 0;          // Timestamp ab wann durchgehend "bad"

        // A/V-Versatz-Diagnose: letzter Log-Zeitpunkt + letzte Rohwerte der
        // Jitter-Buffer-Zaehler. jitterBufferDelay/-EmittedCount sind
        // KUMULATIV seit Call-Beginn -- der reine Quotient waere ein
        // Durchschnitt ueber den ganzen Call und wuerde ein spaeter
        // wachsendes Polster verschlucken. Darum immer die Differenz
        // zweier Messungen rechnen.
        this._lastAvLogAt = 0;
        this._jb = { aDelay: 0, aCount: 0, vDelay: 0, vCount: 0 };
        this._vPrev = null;
        this._vLast = null;
    }

    init() {}

    // ── Start / Stop ───────────────────────────────────────────

    start() {
        this.stop();
        this._lastPacketsReceived = 0;
        this._lastPacketsLost = 0;
        this._currentLevel = 'good';
        this._badSince = 0;
        this._ensureElement();
        if (!this._el) return;

        // Bei Gruppen-Calls ins Grid verschieben, sonst im remoteVideoContainer lassen
        const s = VCS.state;
        if (s.groupCallActive) {
            const grid = document.getElementById('groupVideoGrid');
            if (grid && this._el.parentNode !== grid) grid.appendChild(this._el);
        } else {
            const container = document.getElementById('remoteVideoContainer');
            if (container && this._el.parentNode !== container) container.appendChild(this._el);
        }

        this._el.classList.remove('hidden');
        this._hideReconnect();
        this._interval = setInterval(() => this._poll(), 3000);
    }

    stop() {
        if (this._interval) {
            clearInterval(this._interval);
            this._interval = null;
        }
        if (this._el) this._el.classList.add('hidden');
        this._hideReconnect();
        this._lastPacketsReceived = 0;
        this._lastPacketsLost = 0;
        this._currentLevel = 'good';
        this._badSince = 0;
        // Puffer-Zaehler mit zuruecksetzen: im neuen Call starten sie wieder
        // bei 0, sonst waere die erste Differenz negativ. start() ruft stop().
        this._lastAvLogAt = 0;
        this._jb = { aDelay: 0, aCount: 0, vDelay: 0, vCount: 0 };
        this._vPrev = null;
        this._vLast = null;
    }

    // Stubs für Kompatibilität
    updateVisibility() {}
    resetText() {}

    // ── Private ────────────────────────────────────────────────

    _ensureElement() {
        this._el = document.getElementById('netQualityIndicator');
        if (this._el) {
            this._reconnectBtn = this._el.querySelector('.net-quality-reconnect');
        }
    }

    async _poll() {
        const s = VCS.state;

        // 1:1-Call
        if (s.pc && s.directCallActive) {
            await this._evaluate(s.pc);
            return;
        }

        // Gruppen-Call: schlechteste Verbindung zählt
        if (s.groupCallActive && s.groupCallPeers?.size > 0) {
            let worstLevel = 'good';
            for (const [, peer] of s.groupCallPeers) {
                if (!peer.pc) continue;
                const level = await this._getLevel(peer.pc);
                if (level === 'bad' || (level === 'fair' && worstLevel === 'good')) {
                    worstLevel = level;
                }
            }
            this._applyLevel(worstLevel);
            return;
        }
    }

    async _evaluate(pc) {
        const level = await this._getLevel(pc);
        this._applyLevel(level);
    }

    async _getLevel(pc) {
        try {
            const stats = await pc.getStats();
            let rtt = null;
            let totalPacketsReceived = 0;
            let totalPacketsLost = 0;
            // Rohwerte fuer die A/V-Versatz-Diagnose (siehe _logAvDelay)
            let jbADelay = 0, jbACount = 0, jbVDelay = 0, jbVCount = 0;
            let audioJitter = null;
            // Bildqualitaet fuer dieselbe Zeile: was ankommt (in) und was
            // dieses Geraet verschickt (out). Unscharfes Bild beim Gegenueber
            // steht auf der SENDENDEN Seite -- dort sagt limit, warum der
            // Encoder drosselt (bandwidth / cpu / none).
            const video = {
                inW: 0, inH: 0, inFps: null, inBytes: 0,
                outW: 0, outH: 0, outFps: null, outBytes: 0,
                limit: null, availOutKbps: null
            };

            stats.forEach(report => {
                if (report.type === 'candidate-pair' && (report.state === 'succeeded' || report.nominated)) {
                    if (report.currentRoundTripTime != null) {
                        rtt = report.currentRoundTripTime * 1000; // → ms
                    }
                    if (report.availableOutgoingBitrate != null) {
                        video.availOutKbps = Math.round(report.availableOutgoingBitrate / 1000);
                    }
                }
                if (report.type === 'outbound-rtp' && report.kind === 'video') {
                    video.outBytes += report.bytesSent || 0;
                    if (report.frameWidth) {
                        video.outW = report.frameWidth;
                        video.outH = report.frameHeight;
                    }
                    if (report.framesPerSecond != null) video.outFps = report.framesPerSecond;
                    if (report.qualityLimitationReason) video.limit = report.qualityLimitationReason;
                }
                if (report.type === 'inbound-rtp' && report.kind === 'audio') {
                    totalPacketsReceived += report.packetsReceived || 0;
                    totalPacketsLost += report.packetsLost || 0;
                    jbADelay += report.jitterBufferDelay || 0;
                    jbACount += report.jitterBufferEmittedCount || 0;
                    if (report.jitter != null) audioJitter = report.jitter;
                }
                if (report.type === 'inbound-rtp' && report.kind === 'video') {
                    jbVDelay += report.jitterBufferDelay || 0;
                    jbVCount += report.jitterBufferEmittedCount || 0;
                    video.inBytes += report.bytesReceived || 0;
                    if (report.frameWidth) {
                        video.inW = report.frameWidth;
                        video.inH = report.frameHeight;
                    }
                    if (report.framesPerSecond != null) video.inFps = report.framesPerSecond;
                }
            });

            // Delta-Paketverlust seit letztem Poll
            const deltaReceived = totalPacketsReceived - this._lastPacketsReceived;
            const deltaLost = totalPacketsLost - this._lastPacketsLost;
            this._lastPacketsReceived = totalPacketsReceived;
            this._lastPacketsLost = totalPacketsLost;

            const totalDelta = deltaReceived + deltaLost;
            const lossPercent = totalDelta > 0 ? (deltaLost / totalDelta) * 100 : 0;

            // Nur im 1:1-Call messen. Im Gruppen-Call laeuft _getLevel pro
            // Peer, das wuerde die Zaehler vermischen.
            if (pc === VCS.state.pc) {
                this._updateVideoRates(video);
                this._logAvDelay(jbADelay, jbACount, jbVDelay, jbVCount, audioJitter, rtt, lossPercent);
            }

            // Bewertung
            if (rtt !== null && (rtt > 400 || lossPercent > 10)) return 'bad';
            if (rtt !== null && (rtt > 150 || lossPercent > 3)) return 'fair';
            return 'good';
        } catch (_) {
            return 'good'; // Stats nicht verfügbar → optimistisch
        }
    }

    // Wie lange haengen Ton und Bild gerade im Wiedergabe-Puffer?
    // Das ist die Antwort auf "ich sehe sie sprechen, der Ton kommt spaeter":
    // drift = audioBuf - videoBuf. Positiv heisst, der Ton laeuft hinterher.
    //
    // Beide Zaehler sind kumulativ seit Call-Beginn, darum die Differenz zum
    // letzten Poll -- das ergibt den Puffer der letzten 3 Sekunden statt eines
    // Mittelwerts ueber den ganzen Call.
    //
    // Ausgabe per console.warn: console.log/info sind im Messenger stumm
    // geschaltet (core/error-logger.js), nur warn/error landen ueber den
    // client-log-Channel im Backend-Log.
    _logAvDelay(aDelay, aCount, vDelay, vCount, jitter, rtt, lossPercent) {
        const prev = this._jb;
        const dACount = aCount - prev.aCount;
        const dVCount = vCount - prev.vCount;
        const audioMs = dACount > 0 ? Math.round(((aDelay - prev.aDelay) / dACount) * 1000) : null;
        const videoMs = dVCount > 0 ? Math.round(((vDelay - prev.vDelay) / dVCount) * 1000) : null;
        this._jb = { aDelay, aCount, vDelay, vCount };
        if (audioMs === null) return;

        // Auffaellig = Ton haengt ueber eine halbe Sekunde im Puffer oder
        // laeuft mehr als 300 ms hinter dem Bild her. Dann haeufiger loggen.
        const drift = videoMs !== null ? audioMs - videoMs : 0;
        const auffaellig = audioMs > 500 || drift > 300;
        const now = Date.now();
        if (now - this._lastAvLogAt < (auffaellig ? 5000 : 20000)) return;
        this._lastAvLogAt = now;

        console.warn('[av-delay] audioBuf=' + audioMs + 'ms'
            + ' videoBuf=' + (videoMs === null ? '?' : videoMs + 'ms')
            + ' drift=' + drift + 'ms'
            + ' jitter=' + (jitter === null ? '?' : Math.round(jitter * 1000) + 'ms')
            + ' rtt=' + (rtt === null ? '?' : Math.round(rtt) + 'ms')
            + ' loss=' + lossPercent.toFixed(1) + '%'
            + this._videoQualityText());
    }

    // Bitrate aus der Differenz der kumulativen Byte-Zaehler seit dem
    // letzten Poll (~3 s). Der Rest (Aufloesung, fps, limit) ist ein
    // Momentwert und wird nur fuer die naechste Logzeile gemerkt.
    _updateVideoRates(video) {
        const now = performance.now();
        const prev = this._vPrev;
        let inKbps = null, outKbps = null;
        if (prev && now > prev.at) {
            const sec = (now - prev.at) / 1000;
            if (video.inBytes >= prev.inBytes) inKbps = Math.round(((video.inBytes - prev.inBytes) * 8) / 1000 / sec);
            if (video.outBytes >= prev.outBytes) outKbps = Math.round(((video.outBytes - prev.outBytes) * 8) / 1000 / sec);
        }
        this._vPrev = { at: now, inBytes: video.inBytes, outBytes: video.outBytes };
        this._vLast = { ...video, inKbps, outKbps };
    }

    // Anhang fuer die [av-delay]-Zeile, z.B.
    // " in=640x360@24fps/450kbps out=1280x720@30fps/1200kbps limit=bandwidth avail=1500kbps"
    _videoQualityText() {
        const v = this._vLast;
        if (!v) return '';
        const side = (w, h, fps, kbps) => (w ? w + 'x' + h : '?')
            + '@' + (fps == null ? '?' : Math.round(fps) + 'fps')
            + '/' + (kbps == null ? '?' : kbps + 'kbps');
        return ' in=' + side(v.inW, v.inH, v.inFps, v.inKbps)
            + ' out=' + side(v.outW, v.outH, v.outFps, v.outKbps)
            + ' limit=' + (v.limit || '?')
            + ' avail=' + (v.availOutKbps == null ? '?' : v.availOutKbps + 'kbps');
    }

    _applyLevel(level) {
        if (!this._el) return;
        this._currentLevel = level;
        this._el.className = 'net-quality-indicator net-quality--' + level;

        // Reconnect-Button nach 6s durchgehend "bad" einblenden
        if (level === 'bad') {
            if (!this._badSince) this._badSince = Date.now();
            if (Date.now() - this._badSince >= 6000) {
                this._showReconnect();
            }
        } else {
            this._badSince = 0;
            this._hideReconnect();
        }
    }

    _showReconnect() {
        if (!this._reconnectBtn) return;
        this._reconnectBtn.classList.remove('hidden');
    }

    _hideReconnect() {
        if (!this._reconnectBtn) return;
        this._reconnectBtn.classList.add('hidden');
    }
};
